import { ArrowLeft, Calendar, Clock, MapPin, MessageSquare, Plus, User, Phone, Star, RotateCcw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AppointmentDetailDialog } from "@/components/AppointmentDetailDialog";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import BottomNavigation from "@/components/BottomNavigation";
import { toast } from "sonner";
import { useState } from "react";

interface Appointment {
  id: string;
  serviceTitle: string;
  providerName: string;
  providerAvatar: string;
  date: Date;
  time: string;
  duration: string;
  price: number;
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  location?: string;
  notes?: string;
}

const MyAppointments = () => {
  const navigate = useNavigate();
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Mock appointments data - would come from API/database in real app
  const mockAppointments: Appointment[] = [
    {
      id: "1",
      serviceTitle: "Mindful Life Coaching Session",
      providerName: "Sarah Moonlight",
      providerAvatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      date: new Date(2025, 7, 15), // August 15, 2025
      time: "10:00",
      duration: "90 min",
      price: 85,
      status: 'confirmed',
      location: "Online (Zoom)",
      notes: "Eerste sessie - focus op doelen stellen"
    },
    {
      id: "2", 
      serviceTitle: "Business Strategy Consultation",
      providerName: "Lisa van der Berg",
      providerAvatar: "https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=200",
      date: new Date(2025, 7, 22), // August 22, 2025
      time: "14:30",
      duration: "60 min",
      price: 120,
      status: 'pending',
      location: "Amsterdam Centrum",
      notes: "Marketing strategie bespreken"
    },
    {
      id: "3",
      serviceTitle: "Yoga & Mindfulness Session",
      providerName: "Maria Santos",
      providerAvatar: "https://images.pexels.com/photos/3823495/pexels-photo-3823495.jpeg?auto=compress&cs=tinysrgb&w=200",
      date: new Date(2025, 6, 28), // July 28, 2025 (past)
      time: "09:00",
      duration: "75 min",
      price: 65,
      status: 'completed',
      location: "Vondelpark Studio"
    }
  ];

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Bevestigd';
      case 'pending': return 'In afwachting';
      case 'completed': return 'Voltooid';
      case 'cancelled': return 'Geannuleerd';
      default: return status;
    }
  };

  // Sort appointments by date (upcoming first, then past)
  const sortedAppointments = [...mockAppointments].sort((a, b) => {
    const now = new Date();
    const aFuture = a.date >= now;
    const bFuture = b.date >= now;
    if (aFuture && !bFuture) return -1;
    if (!aFuture && bFuture) return 1;
    return a.date.getTime() - b.date.getTime();
  });

  const upcomingAppointments = sortedAppointments.filter(apt => apt.date >= new Date());
  const pastAppointments = sortedAppointments.filter(apt => apt.date < new Date());

  const handleSendMessage = (appointment: Appointment) => {
    // Navigate to messages with pre-filled conversation
    toast.success(`Opening chat met ${appointment.providerName}...`);
    navigate('/berichten', { 
      state: { 
        providerId: appointment.id,
        providerName: appointment.providerName,
        appointmentId: appointment.id 
      }
    });
  };

  const handleContact = (appointment: Appointment) => {
    // Show contact options
    toast.info(`Contact opties voor ${appointment.providerName}`, {
      description: "Telefoon, email of video call beschikbaar",
      action: {
        label: "Bekijk profiel",
        onClick: () => navigate(`/provider/${appointment.id}`)
      }
    });
  };

  const handleRebook = (appointment: Appointment) => {
    // Navigate to appointment booking with pre-filled service
    toast.success(`Nieuwe afspraak boeken met ${appointment.providerName}...`);
    navigate('/agenda', { 
      state: { 
        serviceId: appointment.id,
        providerId: appointment.id,
        prefill: true
      }
    });
  };

  const handleWriteReview = (appointment: Appointment) => {
    // Show review modal or navigate to review page
    toast.success(`Review schrijven voor ${appointment.serviceTitle}`, {
      description: "Deel je ervaring met andere gebruikers",
      action: {
        label: "Schrijf review",
        onClick: () => {
          // Here you would typically open a review modal or navigate to review page
          console.log("Opening review form for appointment:", appointment.id);
        }
      }
    });
  };

  const handleViewAppointmentDetails = (appointment: Appointment) => {
    console.log("👁️ Opening appointment details for:", appointment.serviceTitle);
    setSelectedAppointment(appointment);
    setViewModalOpen(true);
  };

  const handleViewProvider = (appointment: Appointment) => {
    console.log("👁️ Viewing provider profile:", appointment.providerName);
    navigate(`/provider/${appointment.id}`);
    toast.info(`Profiel van ${appointment.providerName}`, {
      description: "Bekijk alle services en reviews",
      action: {
        label: "Boek nieuwe afspraak",
        onClick: () => navigate('/agenda', { state: { providerId: appointment.id }})
      }
    });
  };
  return (
    <>
      <div className="min-h-screen bg-elegant pb-24">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-cream-300 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center gap-4 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleGoBack}
                className="p-2 hover:bg-cream-100 text-navy-600"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1">
                <h1 className="text-2xl font-serif font-semibold text-navy-800">
                  Mijn Afspraken
                </h1>
                <p className="text-sm text-navy-600">
                  Overzicht van al je geboekte sessies
                </p>
              </div>
              <Button 
                onClick={() => navigate('/marketplace')}
                className="btn-elegant-primary"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nieuwe afspraak
              </Button>
            </div>

            {/* WOM Plaza Header */}
            <div className="rounded-lg p-4 bg-cream-200 border border-cream-300">
              <div className="text-center">
                <div className="mb-2">
                  <h1 className="text-xl font-light tracking-wider text-navy-700">
                    <span className="font-bold text-navy-800">W</span>
                    <span className="mx-1 text-gold-400">·</span>
                    <span className="font-bold text-navy-800">O</span>
                    <span className="mx-1 text-gold-400">·</span>
                    <span className="font-bold text-navy-800">M</span>
                    <span className="ml-3 font-bold text-navy-800">Afspraken</span>
                  </h1>
                </div>
                <p className="text-sm text-navy-600">
                  Je persoonlijke agenda met women on a mission
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="card-premium text-center">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-navy-800">{upcomingAppointments.length}</div>
                <div className="text-sm text-navy-600">Komend</div>
              </CardContent>
            </Card>
            <Card className="card-premium text-center">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-navy-800">{pastAppointments.length}</div>
                <div className="text-sm text-navy-600">Voltooid</div>
              </CardContent>
            </Card>
            <Card className="card-premium text-center">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-navy-800">{mockAppointments.length}</div>
                <div className="text-sm text-navy-600">Totaal</div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Appointments */}
          {upcomingAppointments.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-serif font-semibold text-navy-800 mb-4">
                Komende Afspraken
              </h2>
              <div className="space-y-4">
                {upcomingAppointments.map((appointment) => (
                  <Card key={appointment.id} className="card-premium">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12 cursor-pointer" onClick={() => handleViewProvider(appointment)}>
                            <AvatarImage src={appointment.providerAvatar} alt={appointment.providerName} />
                            <AvatarFallback className="bg-cream-200 text-navy-700">
                              {appointment.providerName.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-navy-800">{appointment.serviceTitle}</h3>
                            <p className="text-sm text-navy-600">met {appointment.providerName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-cream-100"
                            onClick={() => handleViewAppointmentDetails(appointment)}
                            title="👁️ Bekijk afspraak details"
                          >
                            <Eye className="h-4 w-4 text-navy-600" />
                          </Button>
                          <Badge className={getStatusColor(appointment.status)}>
                            {getStatusText(appointment.status)}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-navy-600 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{format(appointment.date, 'eeee d MMMM yyyy', { locale: nl })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{appointment.time} ({appointment.duration})</span>
                        </div>
                        {appointment.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{appointment.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-medium">€{appointment.price}</span>
                        </div>
                      </div>

                      {appointment.notes && (
                        <div className="bg-cream-100 rounded-lg p-3 mb-4">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="h-4 w-4 text-navy-600 mt-0.5" />
                            <div>
                              <p className="text-sm text-navy-700 font-medium">Notities:</p>
                              <p className="text-sm text-navy-600">{appointment.notes}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary flex-1"
                          onClick={() => handleSendMessage(appointment)}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Bericht sturen
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary"
                          onClick={() => handleViewProvider(appointment)}
                          title="👁️ Bekijk provider profiel"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary flex-1"
                          onClick={() => handleContact(appointment)}
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          Contact
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Past Appointments */}
          {pastAppointments.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-serif font-semibold text-navy-800 mb-4">
                Eerdere Afspraken  
              </h2>
              <div className="space-y-4">
                {pastAppointments.map((appointment) => (
                  <Card key={appointment.id} className="card-premium opacity-80">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 cursor-pointer" onClick={() => handleViewProvider(appointment)}>
                            <AvatarImage src={appointment.providerAvatar} alt={appointment.providerName} />
                            <AvatarFallback className="bg-cream-200 text-navy-700">
                              {appointment.providerName.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-navy-800">{appointment.serviceTitle}</h3>
                            <p className="text-sm text-navy-600">met {appointment.providerName}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-cream-100"
                            onClick={() => handleViewAppointmentDetails(appointment)}
                            title="👁️ Bekijk afspraak details"
                          >
                            <Eye className="h-4 w-4 text-navy-600" />
                          </Button>
                          <Badge className={getStatusColor(appointment.status)}>
                            {getStatusText(appointment.status)}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-navy-600 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{format(appointment.date, 'd MMMM yyyy', { locale: nl })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{appointment.time}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary flex-1"
                          onClick={() => handleRebook(appointment)}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Opnieuw boeken
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary"
                          onClick={() => handleViewProvider(appointment)}
                          title="👁️ Bekijk provider profiel"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="btn-elegant-secondary flex-1"
                          onClick={() => handleWriteReview(appointment)}
                        >
                          <Star className="h-4 w-4 mr-2" />
                          Review schrijven
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {mockAppointments.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="h-16 w-16 text-navy-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-navy-800 mb-2">Nog geen afspraken</h3>
              <p className="text-navy-600 mb-6">
                Ontdek geweldige services van women on a mission
              </p>
              <Button 
                onClick={() => navigate('/marketplace')}
                className="btn-elegant-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Eerste afspraak boeken
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Appointment Detail Modal */}
      <AppointmentDetailDialog 
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={(appointment) => {
          setViewModalOpen(false);
          // Navigate to edit or show edit functionality
          toast.info(`Afspraak bewerken: ${appointment.serviceTitle}`, {
            description: "Navigeer naar bewerk pagina",
            action: {
              label: "Bewerk",
              onClick: () => navigate('/agenda', { state: { editAppointment: appointment }})
            }
          });
        }}
        onCancel={(appointment) => {
          setViewModalOpen(false);
          if (window.confirm(`Weet je zeker dat je "${appointment.serviceTitle}" wilt annuleren?`)) {
            toast.success(`Afspraak geannuleerd!`, {
              description: `${appointment.serviceTitle} is geannuleerd.`,
              duration: 4000
            });
          }
        }}
      />
      <BottomNavigation />
    </>
  );
};

export default MyAppointments;